import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiPencil, HiTrash } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../../components/modern";
import type { Column } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";
import './ShippingCompanyIndex.scss';

interface ShippingCompany {
  id: number;
  name: string;
  maxWeight: number;
  isActive: boolean;
}

export const ShippingCompanyIndex: React.FC = () => {
  const { t, i18n } = useTranslation(['shippingCompanies', 'common']);
  const navigate = useNavigate();
  const { token } = useAuth();
  const isRTL = i18n.language === 'ar';

  const [companies, setCompanies] = useState<ShippingCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
    });

    return params.toString();
  };

  const fetchCompanies = async (page: number, size: number) => {
    try {
      setLoading(true);
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/shippingcompany?${query}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('shippingCompanies:messages.fetchError'));
        return;
      }

      setCompanies(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error: any) {
      showErrorToast(t('shippingCompanies:messages.fetchError'));
      console.error('Error fetching shipping companies:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies(currentPage, pageSize);
  }, [currentPage, pageSize, filters]);

  const deleteCompany = async (id: number) => {
    if (!window.confirm(t('shippingCompanies:messages.deleteConfirm'))) {
      return;
    }

    try {
      const response = await adminApi.call(`/shippingcompany/${id}`, 'DELETE');

      if (!response.success) {
        showErrorToast(response.error || t('shippingCompanies:messages.deleteError'));
        return;
      }

      showSuccessToast(t('shippingCompanies:messages.deleteSuccess'));
      fetchCompanies(currentPage, pageSize);
    } catch (error: any) {
      showErrorToast(t('shippingCompanies:messages.deleteError') + ": " + error.message);
      console.error('Error deleting shipping company:', error);
    }
  };

  const handleCreateCompany = () => {
    navigate('/shipping-companies/create');
  };

  const handleEditCompany = (id: number) => {
    navigate(`/shipping-companies/edit/${id}`);
  };

  const handleViewCompany = (id: number) => {
    navigate(`/shipping-companies/${id}`);
  };

  const columns: Column[] = [
    {
      key: 'id',
      title: t('common:table.id'),
      sortable: true,
      width: '80px',
    },
    {
      key: 'name',
      title: t('shippingCompanies:table.name'),
      sortable: true,
      filterable: true,
      filterKey: 'name',
    },
    {
      key: 'maxWeight',
      title: t('shippingCompanies:table.maxWeight'),
      sortable: true,
    },
    {
      key: 'taxProfileName',
      title: 'Tax Profile',
      sortable: true,
      render: (taxProfileName: string | null) => taxProfileName || '—',
    },
    {
      key: 'isActive',
      title: t('shippingCompanies:table.status'),
      sortable: true,
      filterable: true,
      filterKey: 'isActive',
      filterType: 'select',
      filterOptions: [
        { label: t('common:all'), value: '' },
        { label: t('common:active'), value: 'true' },
        { label: t('common:inactive'), value: 'false' },
      ],
      render: (isActive: boolean) => (
        <span className={`badge bg-${isActive ? 'success' : 'danger'}`}>
          {isActive ? t('common:active') : t('common:inactive')}
        </span>
      ),
    },
    {
      key: 'actions',
      title: t('shippingCompanies:table.actions'),
      sortable: false,
      width: '150px',
      align: 'center',
      render: (_, company) => (
        <div className="action-buttons">
          {hasPermission("Update Shipping Company") && (
            <ActionButton
              variant="warning"
              size="sm"
              icon={HiPencil}
              onClick={() => handleEditCompany(company.id)}
              tooltip={t('common:edit')}
            />
          )}
          {hasPermission("Delete Shipping Company") && (
            <ActionButton
              variant="danger"
              size="sm"
              icon={HiTrash}
              onClick={() => deleteCompany(company.id)}
              tooltip={t('common:delete')}
            />
          )}
        </div>
      ),
    },
  ];

  const breadcrumbs = [
    { label: t('shippingCompanies:breadcrumbs.shippingCompanies') }
  ];

  return (
    <PageLayout
      title={t('shippingCompanies:title')}
      subtitle={t('shippingCompanies:subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={companies}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={true}
        refreshable={true}
        addButton={hasPermission("Create Shipping Company") ? {
          label: t('shippingCompanies:addNew'),
          onClick: handleCreateCompany
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters(prev => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onRefresh={() => fetchCompanies(currentPage, pageSize)}
        emptyMessage={t('shippingCompanies:messages.noShippingCompanies')}
      />
    </PageLayout>
  );
};
